import type {Department, Rec, RecordHolding} from 'typings/portaro.be.types';
import type {GridCellEditRequest, RecordRow} from 'src/features/record-grid/lib/types';
import type {ReportReferenceReported} from 'src/features/sutor/pages/reports/report-references.data-service';
import type {ToastMessageService} from 'shared/components/kp-toast-messages/toast-message.service';
import type {RecordGridDataService} from 'src/features/record-grid/services/record-grid.data-service';
import type {ModalDialogService} from 'shared/modal-dialogs/modal-dialog.service';
import type {ReportReferencesDataService} from 'src/features/sutor/pages/reports/report-references.data-service';
import type RecordDataService from '../../../../../record/record.data-service';
import type WalkerService from 'shared/services/walker.service';
import type FinishedResponseInteractionService from 'shared/services/finished-response-interaction.service';
import type RecordService from 'src/features/record/record.service';
import {convertFieldTypeIdToFieldId} from 'shared/utils/record-field-utils';
import {loc} from 'shared/utils/pipes';
import {pipe} from 'core/utils';
import {SUTOR_REFERENCE_STATES, SUTOR_REPORT_STATES} from '../../../../sutor-constants';
import {FOND_REFERENCE, FOND_REPORT} from '../../../../sutor-fonds';

export class SutorReportsProcessingService {
    public static serviceName = 'sutorReportsProcessingService';

    /*@ngInject*/
    constructor(public readonly walker: WalkerService,
                public readonly currentDepartment: Department,
                private recordDataService: RecordDataService,
                private toastMessageService: ToastMessageService,
                private modalDialogService: ModalDialogService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private recordGridDataService: RecordGridDataService,
                private reportReferencesDataService: ReportReferencesDataService,
                private recordService: RecordService) {
    }

    public async getTotalReported(reference: Rec): Promise<ReportReferenceReported> {
        try {
            return await this.reportReferencesDataService.getTotalReportedValues(reference.id);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return null;
        }
    }

    public async getSharableDepartments(): Promise<Department[]> {
        try {
            return await this.reportReferencesDataService.getSharableDepartments();
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return [];
        }
    }

    public async getRefreshedReference(reference: Rec): Promise<Rec | null> {
        try {
            return await this.recordDataService.getById(reference.id);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return null;
        }
    }

    public async markReferenceAsInProgress(reference: Rec, reports: RecordRow[]): Promise<void> {
        try {
            await this.modalDialogService.openModalWindow('confirmationDialog', {text: 'Opravdu chcete označit průvodku stavem ve zpracování? Všechny výkazy na této průvodce budou označeny stavem ve zpracování.'});

            await this.recordGridDataService.editCells({
                cellEditRequests: [
                    {
                        record: reference.id,
                        fieldId: convertFieldTypeIdToFieldId(FOND_REFERENCE.stateFieldId, [0, 0]),
                        setFieldValueRequest: {value: SUTOR_REFERENCE_STATES.IN_PROGRESS}
                    }
                ]
            });

            await this.markReportsWithState(reports, SUTOR_REPORT_STATES.IN_PROGRESS);

            this.toastMessageService.showSuccess('Stav úspěšně změněn na ve zpracování!');
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    public async setTotalHoursOnReference(reference: Rec, totalHours: number): Promise<void> {
        try {
            if (totalHours < 0) {
                this.toastMessageService.showError('Celkový počet hodin nemůže být záporný!');
                return;
            }

            await this.recordGridDataService.editCells({
                cellEditRequests: [
                    {
                        record: reference.id,
                        fieldId: convertFieldTypeIdToFieldId(FOND_REFERENCE.totalHoursFieldId, [0, 0]),
                        setFieldValueRequest: {value: totalHours}
                    }
                ]
            });

            this.toastMessageService.showSuccess(`Celkový počet hodin byl úspěšně nastaven na ${totalHours}!`);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    public async markReferenceAsDone(reference: Rec, reports: RecordRow[]): Promise<void> {
        try {
            await this.modalDialogService.openModalWindow('confirmationDialog', {text: 'Opravdu chcete označit průvodku stavem hotovo? Všechny výkazy na této průvodce budou označeny stavem ke schválení.'});

            await this.recordGridDataService.editCells({
                cellEditRequests: [
                    {
                        record: reference.id,
                        fieldId: convertFieldTypeIdToFieldId(FOND_REFERENCE.stateFieldId, [0, 0]),
                        setFieldValueRequest: {value: SUTOR_REFERENCE_STATES.DONE}
                    }
                ]
            });

            await this.markReportsWithState(reports, SUTOR_REPORT_STATES.TO_CONFIRM);

            this.toastMessageService.showSuccess('Stav úšpěšně změněn na hotový!');
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    private async markReportsWithState(reports: RecordRow[], state: string): Promise<void> {
        try {
            const cellEditRequests: GridCellEditRequest[] = [];

            reports.forEach((report) => {
                cellEditRequests.push({
                    record: report.id,
                    fieldId: convertFieldTypeIdToFieldId(FOND_REPORT.stateFieldId, [0, 0]),
                    setFieldValueRequest: {value: state}
                });
            });

            await this.recordGridDataService.editCells({cellEditRequests});
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    public async shareWithDepartment(record: Rec, targetDepartment: Department): Promise<RecordHolding | null> {
        try {
            const holding = await this.recordService.createRecordHolding(record, targetDepartment);
            this.toastMessageService.showSuccess(`Průvodka byla nasdílena s divizí ${pipe(targetDepartment, loc())}!`);
            return holding;
        } catch {
            return null;
        }
    }

    public async removeSharingWithDepartment(recordHolding: RecordHolding): Promise<boolean> {
        try {
            await this.recordService.deleteRecordHolding(recordHolding);
            this.toastMessageService.showSuccess(`Zrušeno sdílení průvodky s divizí ${pipe(recordHolding.department, loc())}!`);
            return true;
        } catch {
            return false;
        }
    }

    public async deleteCurrentDepartmentHolding(recordHolding: RecordHolding): Promise<boolean> {
        try {
            await this.recordService.deleteRecordHolding(recordHolding);
            this.toastMessageService.showSuccess(`Výkaz byl úspěšně smazán z divize ${pipe(recordHolding.department, loc())}!`);
            return true;
        } catch {
            return false;
        }
    }
}